import { useState } from "react";
import { History, Loader2, TriangleAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Super-admin only — collapse every app's release history into one v2.0.0
 *  entry. Needs to be run once per environment (preview and production have
 *  separate databases). */
export function ChangelogResetCard() {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/changelog/reset-baseline", { confirm });
      setResult(data);
      setConfirm("");
      toast.success(`Changelog reset — ${data.apps_rolled_up} apps rolled up to v2.0.0`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-4 border-amber-200 bg-amber-50/40 p-5 dark:border-amber-500/30 dark:bg-amber-500/5"
          data-testid="changelog-reset-card">
      <div className="flex items-start gap-3">
        <History className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">
              Reset changelog to v2.0.0
            </h3>
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
              Every app ends up with a single v2.0.0 release holding a flat, de-duplicated
              bullet list of all its previous notes (newest first), dated today and released by
              &quot;Admin&quot;. Old entries and entries for deleted apps are purged. Run this once
              per environment.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   placeholder="Type RESET to confirm" className="max-w-[220px] bg-white"
                   data-testid="changelog-reset-confirm" />
            <Button onClick={run} disabled={busy || confirm.trim().toUpperCase() !== "RESET"}
                    data-testid="changelog-reset-run"
                    className="bg-amber-600 text-white hover:bg-amber-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <TriangleAlert className="h-4 w-4" />}
              Reset changelog
            </Button>
          </div>
          {result && (
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300"
               data-testid="changelog-reset-result">
              Done — {result.apps_rolled_up} apps rolled up to v2.0.0,{" "}
              {result.old_entries_removed} old entries purged ({result.apps_versioned} apps versioned).
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

export default ChangelogResetCard;
